import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TravellerWithCollectionMembershipsDTO,
  useGetTravellerBearers,
  useListBearers,
} from '@/api/trafikportalen';
import Button from '../core/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from '@/hooks/useToast';
import { Spinner } from '../core/Spinner';
import { TravelCardCreateForm } from '../Travellers/TravelCardCreateForm';
import { DialogWindow } from '../Dialog';
import { resolveVisibleBearerSelection } from '@/helpers/bearers';
import { BearerListItem, useBearerMapping } from '@/hooks/useBearerMapping';
import { IconPlus } from '@/icons';
import { isValidEmail } from '@/helpers/validation';
import Input from '../core/Input';
import CheckBox from '../core/Checkbox';
import { FormElement } from '../FormElement';
import { BearerSelection } from '../BearerSelection';

interface BearerSelectorForTicketProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  onBearerSelect: (bearerId: string) => void;
  selectedBearer: string;
  onEmailChange?: (email: string) => void;
  onSaveEmailChange?: (saveEmail: boolean) => void;
}

export const BearerSelectorForTicket = ({
  traveller,
  onBearerSelect,
  selectedBearer,
  onEmailChange,
  onSaveEmailChange,
}: BearerSelectorForTicketProps) => {
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [newlyCreatedCardId, setNewlyCreatedCardId] = useState<string>('');
  const [bobcatEmail, setBobcatEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [saveEmail, setSaveEmail] = useState(true);
  const { formatMessage } = useIntl();
  const { mapTravellerBearers } = useBearerMapping();

  const {
    data: travellerBearers,
    isLoading,
    isError: bearersError,
  } = useGetTravellerBearers(traveller.traveller.id);

  const {
    data: emailBearers,
    isLoading: isLoadingEmailBearers,
    isError: emailBearersError,
  } = useListBearers(debouncedEmail ? { email: debouncedEmail } : undefined, {
    query: {
      enabled: isValidEmail(debouncedEmail),
    },
  });

  useEffect(() => {
    if (bearersError || emailBearersError) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte hämta biljettbärare',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [bearersError, emailBearersError, formatMessage]);

  useEffect(() => {
    setBobcatEmail('');
    setDebouncedEmail('');
    setSaveEmail(true);
    onEmailChange?.('');
    onSaveEmailChange?.(true);
  }, [traveller.traveller.id, onEmailChange, onSaveEmailChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(bobcatEmail);
    }, 300);

    return () => clearTimeout(timer);
  }, [bobcatEmail]);

  const selectBearer = useCallback(
    (bearerId: string) => {
      onBearerSelect(bearerId);
    },
    [onBearerSelect],
  );

  const bearers = useMemo<BearerListItem[]>(() => {
    const travellerBearersList = travellerBearers
      ? mapTravellerBearers(travellerBearers)
      : [];

    const emailBearersList: BearerListItem[] = emailBearers
      ? emailBearers.map((bearer) => ({
          ...bearer,
          source: 'private' as const,
        }))
      : [];

    const allBearers = [...travellerBearersList];
    emailBearersList.forEach((emailBearer) => {
      if (!allBearers.some((b) => b.id === emailBearer.id)) {
        allBearers.push(emailBearer);
      }
    });

    return allBearers;
  }, [travellerBearers, emailBearers, mapTravellerBearers]);

  const mostRecentBobcatBearerId = traveller.traveller.favored_bobcat_bearer_id;
  const previousFavoredBearerId = useRef(mostRecentBobcatBearerId);

  useEffect(() => {
    const favoredBearerChanged =
      previousFavoredBearerId.current !== mostRecentBobcatBearerId;
    previousFavoredBearerId.current = mostRecentBobcatBearerId;

    const nextBearerId = resolveVisibleBearerSelection({
      visibleBearers: bearers,
      selectedBearerId: selectedBearer,
      newlyCreatedCardId,
      favoredBearerId: mostRecentBobcatBearerId,
      favoredBearerChanged,
    });

    if (nextBearerId !== selectedBearer) {
      selectBearer(nextBearerId);
    }

    if (
      newlyCreatedCardId &&
      !isLoading &&
      (nextBearerId === newlyCreatedCardId ||
        !bearers.some((bearer) => bearer.id === newlyCreatedCardId))
    ) {
      setNewlyCreatedCardId('');
    }
  }, [
    bearers,
    newlyCreatedCardId,
    mostRecentBobcatBearerId,
    selectedBearer,
    selectBearer,
    isLoading,
  ]);

  if (isLoading) {
    return (
      <div className="flex w-full justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <FormElement
          label={
            <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
          }
          input={
            <Input
              type="email"
              value={bobcatEmail}
              onChange={(e) => {
                setBobcatEmail(e.target.value);
                onEmailChange?.(e.target.value);
              }}
              endAdornment={
                isLoadingEmailBearers ? <Spinner className="size-5" /> : null
              }
              helperTextReserveSpace
              helperText={
                traveller.traveller.bobcat_private_email
                  ? formatMessage(
                      {
                        defaultMessage: 'Nyttjas just nu: {email}',
                      },
                      { email: traveller.traveller.bobcat_private_email },
                    )
                  : undefined
              }
            />
          }
        />
        {bobcatEmail && (
          <CheckBox
            id="save-email-checkbox"
            checked={saveEmail}
            onCheckedChange={(checked) => {
              setSaveEmail(checked === true);
              onSaveEmailChange?.(checked === true);
            }}
            label={formatMessage({
              defaultMessage: 'Spara e-postadressen till resenären vid köp',
            })}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <BearerSelection
          bearers={bearers}
          onBearerSelect={selectBearer}
          selectedBearer={selectedBearer}
          mostRecentBobcatBearerId={mostRecentBobcatBearerId}
        />
      </div>

      <Button
        variant="outline"
        className="w-full"
        onClick={() => setAddCardOpen(true)}
        startIcon={<IconPlus />}
      >
        <FormattedMessage defaultMessage="Lägg till systemägt resekort på resenären" />
      </Button>

      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till systemägt resekort på resenären',
        })}
        content={
          <TravelCardCreateForm
            onOpenChange={setAddCardOpen}
            traveller={traveller}
            onSuccess={(newCard) => {
              setAddCardOpen(false);
              setNewlyCreatedCardId(newCard.bearer_id);
            }}
          />
        }
        onOpenChange={setAddCardOpen}
        open={addCardOpen}
      />
    </div>
  );
};
